(() => {
  if (window.LimitlessInjectorSelectorScriptLoaded) return;
  window.LimitlessInjectorSelectorScriptLoaded = true;

  const rootSelector = '[data-limitless-injector-selector]';

  const defaultConfig = {
    appProxyBasePath: '/apps/limitless-selectors',
    chartPlaceholder: 'Select engine brand',
    valuePlaceholder: 'Select value',
    chooseChartText: 'Select an engine brand to begin.',
    chooseText: 'Select a value to see injector reference details.',
    emptyText: 'No injector reference data was found for this selection.',
    productBasePath: '/products/',
    productHandlePrefix: '',
    productHandleSuffix: '',
    productLinkMode: 'search_exact',
    searchBasePath: '/search',
    showProductLinks: true
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const readConfig = (root) => {
    const configScript = root.querySelector('[data-injector-config]');
    if (!configScript) return { ...defaultConfig };

    try {
      return {
        ...defaultConfig,
        ...JSON.parse(configScript.textContent || '{}')
      };
    } catch (error) {
      return { ...defaultConfig };
    }
  };

  const normalizeProxyBase = (base) => String(base || defaultConfig.appProxyBasePath).replace(/\/+$/, '');

  const buildProxyUrl = (base, path, params = {}) => {
    const proxyBase = normalizeProxyBase(base);
    const url = /^https?:\/\//i.test(proxyBase)
      ? new URL(`${proxyBase}${path}`)
      : new URL(`${proxyBase}${path}`, window.location.origin);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  };

  const fetchJson = async (url) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload || payload.ok === false) {
      throw new Error(payload?.error || `Selector request failed with status ${response.status}`);
    }

    return payload;
  };

  const setOptions = (select, values, placeholder) => {
    select.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.className = 'quick-filter__select-label';
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });

    select.value = '';
  };

  const normalizeProductBase = (base) => {
    const value = String(base || '/products/');
    return value.endsWith('/') ? value : `${value}/`;
  };

  const stripProductNotes = (value) => String(value || '')
    .replace(/\([^)]*\)/g, '')
    .replace(/\[[^\]]*\]/g, '')
    .trim();

  const makeHandle = (value, config) => {
    const clean = stripProductNotes(value)
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return clean ? `${config.productHandlePrefix || ''}${clean}${config.productHandleSuffix || ''}` : '';
  };

  const makeProductUrl = (value, config) => {
    const searchValue = stripProductNotes(value);
    if (!searchValue) return '';

    if (config.productLinkMode === 'product_handle') {
      const handle = makeHandle(value, config);
      return handle ? `${normalizeProductBase(config.productBasePath)}${handle}` : '';
    }

    const searchBase = String(config.searchBasePath || '/search');
    const query = `"${searchValue}"`;
    const separator = searchBase.includes('?') ? '&' : '?';
    return `${searchBase}${separator}type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(query)}`;
  };

  const isProductField = (field) => {
    const lower = String(field || '').toLowerCase();
    return lower === 'new'
      || lower === 'reman'
      || lower === 'injector'
      || lower.includes('part no')
      || lower.includes('assy. part')
      || lower.includes('ecotip')
      || lower.includes('utex');
  };

  const splitProductValues = (value) => String(value || '')
    .split(/[,;/]+/)
    .map((item) => item.trim())
    .filter((item) => item && !/^call for info$/i.test(item));

  const collectProductValues = (row, fields) => {
    const values = [];
    const seen = new Set();

    fields.forEach((field) => {
      if (!isProductField(field) || !row[field]) return;

      splitProductValues(row[field]).forEach((value) => {
        const key = value.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          values.push(value);
        }
      });
    });

    return values;
  };

  const renderEmpty = (results, text) => {
    results.innerHTML = `<div class="limitless-injector-selector__empty">${escapeHtml(text)}</div>`;
  };

  const renderChartMeta = (chartMeta, brandTitle, chartNames) => {
    if (!brandTitle || !chartNames.length) {
      chartMeta.hidden = true;
      chartMeta.innerHTML = '';
      return;
    }

    chartMeta.hidden = false;
    chartMeta.innerHTML = `
      <strong>${escapeHtml(brandTitle)}</strong>
      <span>${escapeHtml(chartNames.join(', '))}</span>
    `;
  };

  const renderRows = (root, matches, config) => {
    const results = root.querySelector('[data-injector-results]');

    if (!matches.length) {
      renderEmpty(results, config.emptyText);
      return;
    }

    results.innerHTML = matches.map(({ chart, row }) => {
      const fields = chart.fields || [];
      const titleValue = `${chart.title} - ${row[chart.keyField] || ''}`.replace(/ - $/, '');
      const details = fields
        .filter((field) => row[field])
        .map((field) => `
          <div>
            <dt>${escapeHtml(field)}</dt>
            <dd>${escapeHtml(row[field])}</dd>
          </div>
        `)
        .join('');

      const products = config.showProductLinks
        ? collectProductValues(row, fields).map((value) => {
          const url = makeProductUrl(value, config);
          return url ? `<a class="limitless-injector-selector__product" href="${url}">${escapeHtml(value)}</a>` : '';
        }).join('')
        : '';

      return `
        <article class="limitless-injector-selector__card">
          <h3>${escapeHtml(titleValue)}</h3>
          <dl class="limitless-injector-selector__details">${details}</dl>
          ${products ? `<div class="limitless-injector-selector__products">${products}</div>` : ''}
        </article>
      `;
    }).join('');
  };

  const showError = (results, error) => {
    console.error('[LimitlessInjectorSelector]', error);
    renderEmpty(results, 'Selector data is temporarily unavailable. Please try again.');
  };

  const initRoot = (root) => {
    if (!root || root.dataset.initialized === 'true') return;

    const config = readConfig(root);
    const chartSelect = root.querySelector('[data-injector-chart]');
    const valueSelect = root.querySelector('[data-injector-value]');
    const valueLabel = root.querySelector('[data-injector-value-label]');
    const chartMeta = root.querySelector('[data-injector-chart-meta]');
    const results = root.querySelector('[data-injector-results]');

    if (!chartSelect || !valueSelect || !results) return;

    root.dataset.initialized = 'true';
    setOptions(chartSelect, [], config.chartPlaceholder);
    setOptions(valueSelect, [], config.valuePlaceholder);
    valueSelect.disabled = true;
    renderChartMeta(chartMeta, '', []);
    renderEmpty(results, 'Loading injector data...');

    fetchJson(buildProxyUrl(config.appProxyBasePath, '/injector/brands'))
      .then((payload) => {
        setOptions(chartSelect, payload.brands || [], config.chartPlaceholder);
        renderEmpty(results, config.chooseChartText);
      })
      .catch((error) => showError(results, error));

    chartSelect.addEventListener('change', () => {
      const selectedBrand = chartSelect.value;
      setOptions(valueSelect, [], config.valuePlaceholder);
      valueSelect.disabled = true;
      if (valueLabel) valueLabel.textContent = config.valuePlaceholder;

      if (!selectedBrand) {
        renderChartMeta(chartMeta, '', []);
        renderEmpty(results, config.chooseChartText);
        return;
      }

      renderEmpty(results, 'Loading selector values...');

      fetchJson(buildProxyUrl(config.appProxyBasePath, '/injector/values', { brand: selectedBrand }))
        .then((payload) => {
          if (chartSelect.value !== selectedBrand) return;

          const chartNames = payload.chartNames || [];
          renderChartMeta(chartMeta, selectedBrand, chartNames);
          setOptions(valueSelect, payload.values || [], payload.valuePlaceholder || config.valuePlaceholder);
          valueSelect.disabled = false;
          if (valueLabel) valueLabel.textContent = payload.valueLabel || config.valuePlaceholder;
          renderEmpty(results, config.chooseText);
        })
        .catch((error) => showError(results, error));
    });

    valueSelect.addEventListener('change', () => {
      const selectedBrand = chartSelect.value;
      const selectedValue = valueSelect.value;

      if (!selectedBrand || !selectedValue) {
        renderEmpty(results, selectedBrand ? config.chooseText : config.chooseChartText);
        return;
      }

      renderEmpty(results, 'Loading injector details...');

      fetchJson(buildProxyUrl(config.appProxyBasePath, '/injector/results', {
        brand: selectedBrand,
        value: selectedValue
      }))
        .then((payload) => {
          if (chartSelect.value !== selectedBrand || valueSelect.value !== selectedValue) return;
          renderChartMeta(chartMeta, selectedBrand, payload.chartNames || []);
          renderRows(root, payload.matches || [], config);
        })
        .catch((error) => showError(results, error));
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
